/**
 * The geometry behind the full-screen viewfinder's crop guide.
 *
 * The preview runs edge to edge and is taller than the stored image, so the
 * live video is drawn with `object-fit: cover` and the screen shows a 3:4
 * guide inside it. The stored frame is exactly what sits inside that guide:
 * the largest centred 3:4 rectangle of the visible part of the video. The
 * guide is an SVG fitted to the preview (`CameraLayout` `CropGuide`), and
 * this module computes the same rectangle in video pixels for the shutter,
 * so the two agree for every preview size and every camera resolution.
 */

/** Width over height of the stored image: portrait 3:4. */
export const CAPTURE_ASPECT = 3 / 4;

export interface Size {
  width: number;
  height: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The largest rectangle of `aspect` (width over height) centred inside `outer`. */
export function centredAspectRect(outer: Rect, aspect: number): Rect {
  const outerAspect = outer.width / outer.height;
  const width = outerAspect > aspect ? outer.height * aspect : outer.width;
  const height = outerAspect > aspect ? outer.height : outer.width / aspect;
  return {
    x: outer.x + (outer.width - width) / 2,
    y: outer.y + (outer.height - height) / 2,
    width,
    height,
  };
}

/**
 * The part of a video frame that `object-fit: cover` shows inside a view of
 * size `view`, in video pixels. A view with no size (not laid out, or a test
 * environment) shows the whole frame.
 */
export function coverVisibleRect(video: Size, view: Size): Rect {
  if (view.width <= 0 || view.height <= 0) return { x: 0, y: 0, width: video.width, height: video.height };
  const scale = Math.max(view.width / video.width, view.height / video.height);
  const width = Math.min(video.width, view.width / scale);
  const height = Math.min(video.height, view.height / scale);
  return { x: (video.width - width) / 2, y: (video.height - height) / 2, width, height };
}

/**
 * The source rectangle the shutter draws from the video: the crop guide the
 * operator saw, mapped into video pixels and rounded to whole pixels.
 */
export function captureCropRect(video: Size, view: Size, aspect = CAPTURE_ASPECT): Rect {
  const crop = centredAspectRect(coverVisibleRect(video, view), aspect);
  const x = Math.max(0, Math.round(crop.x));
  const y = Math.max(0, Math.round(crop.y));
  return {
    x,
    y,
    width: Math.max(1, Math.min(video.width - x, Math.round(crop.width))),
    height: Math.max(1, Math.min(video.height - y, Math.round(crop.height))),
  };
}
